import { FC, useState } from "react";
import { FieldErrors, useFormContext, useWatch } from "react-hook-form";
import BMView from "../../../components/View/BMView";
import { Employee } from "../common/TravelerPicker/types";
import CalendarPicker from "./CalendarPicker";
import HotelCitySearch from "./Search/HotelCitySearch";
import SearchFormCard from "./SearchFormCard";
import { HotelSearchFormResource, HotelSearchFormValues } from "./types";

export type PreviousHotelSearchData = {
  geoName: string;
  type: string;
  id: string;
  checkInDate: string;
  checkOutDate: string;
};
export type SelectedPreviousHotelSearchData = Pick<
  HotelSearchFormValues,
  "geoName" | "type" | "id" | "checkInDate" | "checkOutDate"
>;

type Props = {
  locale: string;
  resource: HotelSearchFormResource;
  previousSearchData?: PreviousHotelSearchData[];
  AddEmployeeWidget?: FC<{ addSelectedEmployee: (employee: Employee) => void }>;
  onSubmitForm: (values: HotelSearchFormValues) => void;
  onErrorForm: (errors: FieldErrors<HotelSearchFormValues>) => void;
  selectPreviousSearchData: (
    searchData: SelectedPreviousHotelSearchData
  ) => void;
  userLoggedIn: {
    name: string;
    email: string;
  };
  permissions: {
    travelerPicker: {
      canBookNonEmployee: boolean;
      canBookForOwn: boolean;
      canBookForOthers: boolean;
      canCreateEmployee: boolean;
    };
  };
};

/**
 *
 * To use this component, you will need to wrap this component with the following Components:
 * - `FormProvider` from `react-hook-form`
 * - `HotelAutocompleteProvider` from `submodule:ctv-web-core/src/providers/api/HotelAutocompleteContext`
 * - `TravelerPickerProvider` from `submodule:ctv-web-core/src/providers/api/TravelerPickerContext`
 * - `RecentlyViewedHotelsProvider` from `submodule:ctv-web-core/src/providers/api/RecentlyViewedHotelsContext`
 */
export default function HotelSearchForm(props: Props) {
  const {
    resource,
    previousSearchData,
    AddEmployeeWidget,
    permissions,
    userLoggedIn,
    onErrorForm,
    onSubmitForm,
    selectPreviousSearchData,
  } = props;

  const [showSearch, setShowSearch] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  const { getValues } = useFormContext<HotelSearchFormValues>();
  const calendarPickerInitialValues = {
    checkInDate: getValues("checkInDate"),
    checkOutDate: getValues("checkOutDate"),
  };

  return (
    <BMView>
      <SearchFormCard
        onSearchFocus={() => setShowSearch(true)}
        onCalendarFocus={() => setShowCalendar(true)}
        onSubmitForm={onSubmitForm}
        onErrorForm={onErrorForm}
        AddEmployeeWidget={AddEmployeeWidget}
        permissions={permissions}
        userLoggedIn={userLoggedIn}
      />
      <HotelCitySearch
        resource={resource}
        isVisible={showSearch}
        closeDialog={() => setShowSearch(false)}
        previousSearchData={previousSearchData}
        selectPreviousSearchData={selectPreviousSearchData}
      />
      <CalendarPicker
        initialValues={calendarPickerInitialValues}
        isVisible={showCalendar}
        closeDialog={() => setShowCalendar(false)}
      />
    </BMView>
  );
}
